/*
 * video — Level 2 (CSP-build) privacy-friendly video embed.
 *
 * Renders a poster image with a play button. On click it swaps in a lazily
 * created <iframe> for the YouTube/Vimeo URL, so the third-party player (and its
 * cookies) only load on explicit user intent. No iframe is in the DOM until then.
 *
 * The player URL is resolved SERVER-SIDE by the `embed_url` Antlers modifier
 * (App\Modifiers\EmbedUrl) and read from `data-embed-url` — one source of truth,
 * no URL-parsing regex here. `data-video-url` (the raw authored URL) is the
 * fallback source if a caller renders this component without the modifier. On
 * play we append the autoplay intent (autoplay=1 + muted, required by autoplay
 * policies) — the only part of the URL that depends on the user's click.
 *
 * Registered in resources/js/app.js as: Alpine.data('video', video)
 */
export default function video() {
    return {
        playing: false,
        embedUrl: '',

        init() {
            // Prefer the server-resolved embed URL; fall back to the raw URL so
            // the iframe still loads something if the modifier wasn't applied.
            this.embedUrl =
                this.$root.dataset.embedUrl || this.$root.dataset.videoUrl || '';
        },

        /**
         * Append the autoplay-on-click intent to an already-resolved embed URL.
         * YouTube requires `mute=1`, Vimeo `muted=1`, for autoplay to be honoured.
         */
        withAutoplay(url) {
            const sep = url.includes('?') ? '&' : '?';

            if (url.includes('youtube-nocookie.com') || url.includes('youtube.com')) {
                return `${url}${sep}autoplay=1&mute=1`;
            }
            if (url.includes('player.vimeo.com')) {
                return `${url}${sep}autoplay=1&muted=1`;
            }

            return url;
        },

        play() {
            if (!this.embedUrl || this.playing) {
                return;
            }
            // Alpine CSP intentionally refuses directives on iframe elements.
            // Keep the inert template directive-free and create it on user intent.
            const frame = this.$refs.playerTemplate.content.firstElementChild.cloneNode(true);
            frame.src = this.withAutoplay(this.embedUrl);
            this.$refs.playerHost.replaceChildren(frame);
            this.playing = true;
        },

        destroy() {
            this.$refs.playerHost?.replaceChildren();
            this.playing = false;
        },
    };
}
